import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import type { CopilotQueryFlowResult } from './useCopilotCommunication.types';

export interface BaseCopilotDataFlowResult {
  answerTokens: string[];
  setAnswerTokens: Dispatch<SetStateAction<string[]>>;
  isFetching: boolean;
  setIsFetching: Dispatch<SetStateAction<boolean>>;
  responseType: CopilotQueryFlowResult['responseType'];
  setResponseType: Dispatch<SetStateAction<CopilotQueryFlowResult['responseType']>>;
  error: Error | null;
  setError: Dispatch<SetStateAction<Error | null>>;
  response: string;
}

export interface UseBaseCopilotDataFlowProps {
  /** Called alongside the internal reset, allowing mode-specific hooks to clear their own extra state. */
  onReset?: () => void;
}

/**
 * Shared state foundation for all copilot data-flow hooks.
 * Manages answer tokens, fetching status, response type, and error state.
 * Automatically resets state between queries (when isFetching flips to false).
 *
 * Pass `onReset` to clear any additional state owned by the specific hook (e.g. citations, dateExtraction).
 * Each mode-specific hook calls this, then adds its own runQuery logic on top.
 */
export const useBaseCopilotDataFlow = ({ onReset }: UseBaseCopilotDataFlowProps = {}): BaseCopilotDataFlowResult => {
  const [answerTokens, setAnswerTokens] = useState<string[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [responseType, setResponseType] = useState<CopilotQueryFlowResult['responseType']>(null);
  const [error, setError] = useState<Error | null>(null);

  const response = useMemo(() => answerTokens.join(''), [answerTokens]);

  const reset = useCallback(() => {
    setError(null);
    setAnswerTokens([]);
    setResponseType(null);
    onReset?.();
  }, [onReset]);

  useEffect(() => {
    if (!isFetching) {
      reset();
    }
  }, [isFetching]);

  return {
    answerTokens,
    setAnswerTokens,
    isFetching,
    setIsFetching,
    responseType,
    setResponseType,
    error,
    setError,
    response,
  };
};
